export const SUBSCRIPTION_PERIODS = [
    { value: '1', selectLabel: 'ЗА МЕСЯЦ', pillLabel: '1 мес.' },
    { value: '3', selectLabel: 'ЗА 3 МЕСЯЦА', pillLabel: '3 мес.' },
    { value: '6', selectLabel: 'ЗА 6 МЕСЯЦЕВ', pillLabel: '6 мес.' },
    { value: '12', selectLabel: 'ЗА 12 МЕСЯЦЕВ', pillLabel: '12 мес.' },
    { value: '24', selectLabel: 'ЗА 24 МЕСЯЦА', pillLabel: '24 мес.' },
] as const

export type TariffPeriodDiscountSource = {
    complexPrice?: {
        monthPeriod?: Record<string, unknown> | null
    } | null
}

function isMonthPeriodAvailable(monthPeriod: Record<string, unknown>, period: string): boolean {
    return monthPeriod[period] != null
}

export type SubscriptionPeriodOptions = {
    // Скрыть помесячную оплату (для клиентов на пробном тарифе). Если после фильтра
    // не остаётся ни одного периода — фильтр не применяется, чтобы не сломать витрину.
    excludeMonthly?: boolean
}

function applyPeriodOptions(periods: string[], options: SubscriptionPeriodOptions): string[] {
    if (!options.excludeMonthly) return periods
    const withoutMonthly = periods.filter(value => value !== '1')
    return withoutMonthly.length > 0 ? withoutMonthly : periods
}

function readPeriodDiscountPercent(
    monthPeriod: Record<string, unknown> | null | undefined,
    period: string
): number | null {
    const periodData = monthPeriod?.[period]
    if (!periodData || typeof periodData !== 'object' || Array.isArray(periodData)) return null

    const percent = Number((periodData as Record<string, unknown>).discountPercent)
    if (!Number.isFinite(percent) || percent <= 0) return null

    return Math.round(percent)
}

export function getAvailableSubscriptionPeriods(
    tariff?: TariffPeriodDiscountSource | null,
    options: SubscriptionPeriodOptions = {}
): readonly string[] {
    const monthPeriod = tariff?.complexPrice?.monthPeriod
    if (!monthPeriod || typeof monthPeriod !== 'object' || Array.isArray(monthPeriod)) {
        return applyPeriodOptions(SUBSCRIPTION_PERIODS.map(({ value }) => value), options)
    }

    const available = SUBSCRIPTION_PERIODS
        .filter(({ value }) => isMonthPeriodAvailable(monthPeriod, value))
        .map(({ value }) => value)

    return applyPeriodOptions(
        available.length > 0 ? available : SUBSCRIPTION_PERIODS.map(({ value }) => value),
        options
    )
}

export function resolveSubscriptionPeriod(
    period: string | null | undefined,
    tariff?: TariffPeriodDiscountSource | null,
    options: SubscriptionPeriodOptions & { preferredFallback?: string } = {}
): string {
    const { preferredFallback = '12', ...periodOptions } = options
    const available = getAvailableSubscriptionPeriods(tariff, periodOptions)
    if (period && available.includes(period)) return period
    if (available.includes(preferredFallback)) return preferredFallback
    return available[available.length - 1]
}

export function getPeriodDiscountPercent(
    tariff: TariffPeriodDiscountSource | null | undefined,
    period: string
): number | null {
    if (period === '1') return null

    return readPeriodDiscountPercent(tariff?.complexPrice?.monthPeriod, period)
}

export function formatPeriodDiscountForSelect(percent: number | null): string | null {
    if (!percent || percent <= 0) return null
    return `(-${percent}%)`
}

export function formatPeriodDiscountForPill(percent: number | null): string | null {
    if (!percent || percent <= 0) return null
    return `-${percent}%`
}

export function getSubscriptionPeriodSelectItems(
    tariff?: TariffPeriodDiscountSource | null,
    options: SubscriptionPeriodOptions = {}
) {
    const available = new Set(getAvailableSubscriptionPeriods(tariff, options))

    return SUBSCRIPTION_PERIODS
        .filter(({ value }) => available.has(value))
        .map(({ value, selectLabel }) => {
            const discountLabel = formatPeriodDiscountForSelect(getPeriodDiscountPercent(tariff, value))
            return {
                value,
                name: discountLabel ? `${selectLabel} ${discountLabel}` : selectLabel,
            }
        })
}

export function getSubscriptionPeriodPillItems(
    tariff?: TariffPeriodDiscountSource | null,
    options: SubscriptionPeriodOptions = {}
) {
    const available = new Set(getAvailableSubscriptionPeriods(tariff, options))

    return SUBSCRIPTION_PERIODS
        .filter(({ value }) => available.has(value))
        .map(({ value, pillLabel }) => ({ value, label: pillLabel }))
}

export function getSubscriptionPeriodDiscountLabel(
    period: string,
    tariff?: TariffPeriodDiscountSource | null
): string | null {
    return formatPeriodDiscountForPill(getPeriodDiscountPercent(tariff, period))
}

/** 2-месячный период для сравнения считается месячным (первое списание за 2 месяца). */
export function comparableSubscriptionPeriodMonths(
    period: string | number | null | undefined
): number {
    const months = Number(period)
    if (!Number.isFinite(months) || months <= 0) return 0
    return months === 2 ? 1 : months
}
